import {Endpoint, EndpointAddress, KubernetesClient} from './KubernetesClient.ts';
import {type DiscoveryNode} from '../discovery/index.ts';
import {type Address, fromIpAddress} from '../cluster/Address.ts';
import {type Logger} from '../logging.ts';
import {KubernetesEndpointResolver} from './KubernetesEndpointResolver.ts';
import {KubernetesConfig} from './KubernetesConfig.ts';
import {NetworkConfig} from '../config/Config.ts';
import {type ClusterTopologyIntentTracker} from '../instance/ClusterTopologyIntentTracker.ts';
import {SimpleDiscoveryNode} from '../discovery/SimpleDiscoveryNode.ts';

function buildKubernetesClient(config: KubernetesConfig, clusterTopologyIntentTracker?: ClusterTopologyIntentTracker): KubernetesClient {
    return new KubernetesClient(config.namespace, config.kubernetesMasterUrl, config.exposeExternallyMode, config.tokenProvider, config.servicePerPodLabelName, config.servicePerPodLabelValue, clusterTopologyIntentTracker);
}

export class KubernetesApiEndpointResolver extends KubernetesEndpointResolver {

    private readonly client: KubernetesClient;
    private readonly serviceName?: string;
    private readonly port: number = 0;
    private readonly serviceLabel?: string;
    private readonly serviceLabelValue?: string;
    private readonly podLabel?: string;
    private readonly podLabelValue?: string;
    private readonly resolveNotReadyAddresses?: boolean;

    static of(logger: Logger, config: KubernetesConfig, tracker?: ClusterTopologyIntentTracker): KubernetesApiEndpointResolver {
        return new KubernetesApiEndpointResolver(logger, buildKubernetesClient(config, tracker),
            config.serviceName, config.servicePort, config.serviceLabelName,
            config.serviceLabelValue, config.podLabelName, config.podLabelValue, config.resolveNotReadyAddresses);
    }

    /*testing*/ constructor(logger: Logger,
                            client: KubernetesClient,
                            serviceName?: string,
                            port: number = 0,
                            serviceLabel?: string,
                            serviceLabelValue?: string,
                            podLabel?: string,
                            podLabelValue?: string,
                            resolveNotReadyAddresses?: boolean) {
        super(logger);
        this.client = client;
        this.serviceName = serviceName;
        this.port = port;
        this.serviceLabel = serviceLabel;
        this.serviceLabelValue = serviceLabelValue;
        this.podLabel = podLabel;
        this.podLabelValue = podLabelValue;
        this.resolveNotReadyAddresses = resolveNotReadyAddresses;
    }

    async resolve(): Promise<DiscoveryNode[]> {
        if (this.serviceName) {
            return await this.getSimpleDiscoveryNodes(await this.client.endpointsByName(this.serviceName));
        }
        else if (this.serviceLabel) {
            return await this.getSimpleDiscoveryNodes(await this.client.endpointsByServiceLabel(this.serviceLabel, this.serviceLabelValue));
        }
        else if (this.podLabel) {
            return await this.getSimpleDiscoveryNodes(await this.client.endpointsByPodLabel(this.podLabel, this.podLabelValue));
        }
        return await this.getSimpleDiscoveryNodes(await this.client.endpoints());
    }

    private async getSimpleDiscoveryNodes(endpoints: readonly Endpoint[]): Promise<DiscoveryNode[]> {
        const discoveredNodes = new Array<DiscoveryNode>();
        for (const endpoint of endpoints) {
            await this.addAddress(discoveredNodes, endpoint);
        }
        return discoveredNodes;
    }

    private async addAddress(discoveredNodes: Array<DiscoveryNode>, endpoint: Endpoint): Promise<void> {
        if (this.resolveNotReadyAddresses === true || endpoint.ready) {
            const privateAddress = await this.createAddress(endpoint.privateAddress, this.resolvePort.bind(this));
            const publicAddress = await this.createAddress(endpoint.publicAddress, this.resolvePortPublic.bind(this));
            discoveredNodes.push(new SimpleDiscoveryNode(privateAddress, publicAddress, new Map<string, string>()));
        }
    }

    private async createAddress(address: EndpointAddress | undefined, portResolver: (address: EndpointAddress) => number): Promise<Address> {
        if (address === undefined) {
            return undefined;
        }
        const ip = address.ip;
        const ipAddress = await this.mapAddress(ip);
        const port = portResolver(address);
        return fromIpAddress(ipAddress, port);
    }

    private resolvePort(address: EndpointAddress) {
        if (this.port > 0) {
            return this.port;
        }
        if (address.port) {

            return address.port;
        }
        return NetworkConfig.DEFAULT_PORT;
    }

    private resolvePortPublic(address: EndpointAddress) {
        if (address.port) {
            return address.port;
        }
        if (this.port > 0) {
            return this.port;
        }
        return NetworkConfig.DEFAULT_PORT;
    }
}
